/* eslint-disable react/react-in-jsx-scope */
import { XStack } from '@lyra/core/components'
import AreaChart from '@lyra/core/components/AreaChart'
import BodyText from '@lyra/core/components/BodyText'
import Card from '@lyra/core/components/Card'
import LabelText from '@lyra/core/components/LabelText'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatUSD from '@lyra/core/utils/formatUSD'
import { InstrumentType } from '@lyra/web/constants/instruments'
import { getOptionBreakEvenPrice, getOptionProjectedSettlePnl } from '@lyra/web/src/utils/order'
import { parseInstrumentName } from '@lyra/web/utils/instruments'
import React, { useMemo } from 'react'
import { StackProps } from 'tamagui'

type PayoffDataPoint = {
  x: number
  y: number
  spotPrice: number
  profit?: number
  loss?: number
}

export type PayoffLegData = {
  instrument_name: string
  direction: 'buy' | 'sell'
  premiums: number
  amount: number
}

type Props = {
  legs: [PayoffLegData, ...PayoffLegData[]]
  spotPrice: number
} & StackProps

const NUM_PAYOFF_CHART_POINTS = 400

function getPayoffChartData(
  legs: [PayoffLegData, ...PayoffLegData[]],
  spotPrice: number
): PayoffDataPoint[] {
  const breakEvens = legs.map((data) =>
    getOptionBreakEvenPrice(
      data.instrument_name,
      data.amount !== 0 ? data.premiums / data.amount : 0
    )
  )

  const strikePrices = filterNulls(
    legs.map((data) => {
      const instrument = parseInstrumentName(data.instrument_name)
      if (instrument?.type !== InstrumentType.Options) {
        return null
      }
      return instrument.strikePrice
    })
  )

  const maxSpotPrice = Math.max(...breakEvens, ...strikePrices, spotPrice)
  const minSpotPrice = Math.min(...breakEvens, ...strikePrices, spotPrice)
  const avgBreakEven = breakEvens.reduce((sum, be) => sum + be, 0) / breakEvens.length

  const spotMin = Math.min(avgBreakEven * 0.7, minSpotPrice * 0.9)
  const spotMax = Math.max(avgBreakEven * 1.3, maxSpotPrice * 1.1)

  const spotPriceInterval = (spotMax - spotMin) / NUM_PAYOFF_CHART_POINTS

  const payoffMap = new Map<number, number>()

  Array.from({ length: NUM_PAYOFF_CHART_POINTS }, (_, index) => {
    const spotPrice = spotMin + index * spotPriceInterval
    const totalPayoff = legs.reduce((acc, option) => {
      return (
        acc +
        getOptionProjectedSettlePnl(
          option.instrument_name,
          option.direction,
          spotPrice,
          option.premiums / option.amount,
          option.amount
        )
      )
    }, 0)
    payoffMap.set(spotPrice, totalPayoff)
  })

  const data = Array.from(payoffMap.entries()).map(([spotPrice, payoff]) => ({
    spotPrice,
    x: spotPrice,
    y: payoff,
    profit: payoff >= 0 ? payoff : undefined,
    loss: payoff < 0 ? payoff : undefined,
  }))

  return data
}

const PayoffChart = ({ legs, spotPrice, ...stackProps }: Props) => {
  // Data calculation
  const data = useMemo(() => getPayoffChartData(legs, spotPrice), [legs, spotPrice])

  const firstInstrumentName = legs[0].instrument_name
  const marketId = parseInstrumentName(firstInstrumentName)?.marketId

  const hasNonOptions = useMemo(
    () =>
      legs.some((leg) => parseInstrumentName(leg.instrument_name)?.type !== InstrumentType.Options),
    [legs]
  )
  const isMissingAmount = useMemo(() => legs.some((leg) => !leg.amount || !leg.premiums), [legs])

  const spotDataPoint = useMemo(() => {
    return data.find((dat) => dat.spotPrice >= spotPrice)
  }, [data, spotPrice])

  if (hasNonOptions || isMissingAmount) {
    return (
      <XStack {...stackProps} alignItems="center" justifyContent="center">
        <LabelText>
          {hasNonOptions ? 'Options not supported' : 'Missing amount or premium'}
        </LabelText>
      </XStack>
    )
  }

  return (
    <AreaChart
      {...stackProps}
      chartMargin={{ top: 6, bottom: 6 }}
      dataKeys={[
        { key: 'profit', color: 'green', type: 'linear' },
        { key: 'loss', color: 'red', type: 'linear' },
      ]}
      data={data}
      yAxisProps={{
        hide: true,
      }}
      xAxisProps={{
        hide: true,
      }}
      showGrid
      referenceLinesProps={[
        {
          x: spotPrice,
          id: 'spot-price',
          label: `${marketId} ${formatUSD(spotPrice)}`,
          color: 'hairline',
          textColor: 'secondaryText',
          position: !spotDataPoint || spotDataPoint.y >= 0 ? 'insideBottom' : 'insideTop',
        },
      ]}
      xAxisDataKey="spotPrice"
      tooltipProps={{
        content: ({ active, payload, label }) => {
          if (active && payload && payload.length && typeof label === 'number') {
            const profitOrLoss = payload[0].value! as number
            const thisSpotPrice = label

            return (
              <Card backgroundColor="$appBg90">
                <XStack gap="$2" justifyContent="space-between">
                  <LabelText>{profitOrLoss >= 0 ? 'Expected Profit' : 'Expected Loss'}</LabelText>
                  <BodyText color={profitOrLoss >= 0 ? 'green' : 'red'}>
                    {formatUSD(profitOrLoss, { showSign: true })}
                  </BodyText>
                </XStack>
                <XStack gap="$2" justifyContent="space-between">
                  <LabelText>{marketId} Price</LabelText>
                  <BodyText>{formatUSD(thisSpotPrice)}</BodyText>
                </XStack>
              </Card>
            )
          }
        },
      }}
    />
  )
}

export default PayoffChart
